import { BasicEntity, TranslatedEmb } from '@wizeoo/akm-message-lib';
import { TIME_UNIT } from '../../model/enums';
import { ChronocodeVO } from '../core/chronocode.vo';
import { WizValidationError } from '@wizeoo/akm-component-lib';
import { WizDateUtils } from '@wizeoo/akm-core-lib';
import { TranslatableLabelVO } from '../common/translation/translatable-label.vo';
import { IVo } from '@wizeoo/akm-compute-lib';
import { Model } from '../../model/model';
import { TimeGapVO } from '../core/timegap.vo';
import { ChronocodeGenerator } from '../../manager/core/chronocode.generator';

/**
 * Classe de modèle de chronocode pour génération d'arbre
 */
export class ChronocodeTemplateVO implements IVo {
  
  protected _model: Model = null;
  public get model(): Model {
    return this._model as Model;
  }
  public set model(value: Model) {
      this._model = value;
  }
  
  constructor(model: Model) {
    this._model = model;
  }
  
  /** Domaine */
  public domain: string;
  public businessUnitCode: string;
  public parentCode: string;
  public shortCode: string;
  public timeGapCode: string;
  public startDate: number;
  public endDate: number;
  public label: TranslatedEmb = null;
  public level: number; // niv, commence à 1

  public codeRegex: string; // expression pour la génération des codes (niveau 2+)

  public parentTemplate: ChronocodeTemplateVO; // référence au parent pour les subs
  public maxTimeWeight: number = 0; // le poids max, dégressif pour chaque enfant selon le poids du parent
  public requestedChildren: number = 0; // nombre d'enfants demandés pour un découpage undefined
  public hasChild: boolean = false; // j'ai un enfant, plus de modifs de périodes

  public simulatedChronocodes: Map<string, ChronocodeVO> = new Map<string, ChronocodeVO>(); // lors de la simulation, enregistrement des chronocodes de ce level
  public customPeriods: ChronocodeVO[] = null; // en cas de customisation des périodes Undefined, dic[chronoParent] = Chronos[]

  // pour contrat Ivo
  public id: number;
  public source: BasicEntity;
  public uid;



  /* ################################################
            accesseurs composites
   ################################################ */

  /** Les modèles pour les niveaux inférieurs */
  public subLevels: ChronocodeTemplateVO[] = [];

  public get subLevelMap(): Map<number, ChronocodeTemplateVO> {
    return new Map(this.subLevels.map((item) => [item.level, item]));
  }

  /** Acces au vo time gap */
  public get timeGapVO(): TimeGapVO {
    return this.model.chronocodeEngine.getTimeGap(this.timeGapCode);
  }

  /** pour le level 1, vérifier que les dates permettent la création d'enfants */
  public mainChronoIsValid(): boolean {
    let result: boolean = true;
    if (!this.timeGapCode || !this.startDate || !this.endDate) {
      result = false;
    } else {
      const startDate = WizDateUtils.numberToDate(this.startDate);
      const endDate = WizDateUtils.numberToDate(this.endDate);
      const nbDays: number = WizDateUtils.dayDiff(startDate, endDate);
      if (nbDays <= 1) {
        // il faut pouvoir splitter
        result = false;
      }
    }

    return result;
  }

  /** Flag vrai niveau (avec découpage défini ou "undefined") */
  public get hasTimeGap(): boolean {
    let result: boolean = false;
    if (this.timeGapCode !== null && this.timeGapCode !== undefined && this.timeGapCode !== TIME_UNIT.UNDEFINED) {
      // temps défini
      result = true;
    }
    if (this.timeGapCode === TIME_UNIT.UNDEFINED && this.requestedChildren > 0) {
      // temps indéfini et nombre de tranches ok
      result = true;
    }
    return result;
  }

  /* ################################################
            constructions
   ################################################ */

  /** ajout d'un niveau supplémentaire (forcément à la suite) */
  public addSubLevel(): void {
    const newLevel: ChronocodeTemplateVO = new ChronocodeTemplateVO(this.model);
    newLevel.level = this.subLevels.length + 2;
    newLevel.domain = this.domain;
    newLevel.businessUnitCode = this.businessUnitCode;

    // prise du poids temporel en fonction du parent
    const parent: ChronocodeTemplateVO = this.subLevels.length > 0 ? this.subLevelMap.get(newLevel.level - 1) : this;
    parent.hasChild = true; // on flag le parent
    newLevel.parentTemplate = parent;
    newLevel.maxTimeWeight = this.getTemporalWeight(parent);
    this.subLevels.push(newLevel);
  }

  /** suppression d'un niveau, va bouziller toute la pile (appel sur le mainTemplate) */
  public removeSubLevel(level: number): void {
    if (this.subLevelMap.has(level)) {
      this.subLevelMap.get(level).parentTemplate.hasChild = false;
      this.subLevels.splice(level - 2, 100);
    }
  }

  /**
   * Recherche du poids temporel minimal au dessus d'un niveau donné
   * @param parent: ChronocodeTemplateVO le niveau à partir du quel on cherche le poids minimal
   * @return number le poids de la temporalité défini du niveau plus haut ou égale à celui cherché
   */
  private getTemporalWeight(parent: ChronocodeTemplateVO): number {
    let result: number = parent.timeGapVO.temporalWeight;
    if (!result || result === 0) {
      // chronocode undefined
      if (!!parent.startDate && !!parent.endDate) {
        // valable pour le level2 uniquement
        const startDate = WizDateUtils.numberToDate(parent.startDate);
        const endDate = WizDateUtils.numberToDate(parent.endDate);
        result = WizDateUtils.dayDiff(startDate, endDate);
      } else {
        // undefined/ sinon on prend le poids décomposés / undefined / period sur le poids du parent
        if (!!parent.customPeriods && parent.customPeriods.length > 0) {
          result = parent.maxTimeWeight;
        } else {
          result = Math.ceil(parent.maxTimeWeight / parent.requestedChildren);
        }
      }
    }
    return result;
  }

  /* ################################################
            accesseurs composites
   ################################################ */

  /** reset depuis écran de customisation */
  public resetCustomPeriods(): void {
    this.simulatedChronocodes = new Map<string, ChronocodeVO>();
    this.customPeriods = null;
    this.computeMaxTimeWeight(); // calcul du poids
  }

  /** construction de la map des périodes indefinies, pour customisation des labels / dates */
  public buildUndefinedPeriods(): void {
    if (!this.customPeriods) {
      // pas de périodes, on déclenche une simulation (alimente this.simulatedChronocodes)
      ChronocodeGenerator.generateChronocode(this.model)
    }
    this.customPeriods = Array.from(this.simulatedChronocodes.values()).sort((a, b) =>
      a.startDate >= b.startDate ? 1 : -1
    );
    this.computeMaxTimeWeight(); // calcul du poids
  }

  /**
   * sur simulation, ajout des chronos de ce level
   * @param chron chronocodeVO[]
   */
  public addSimulated(chron: ChronocodeVO[]): void {
    chron.forEach((chr: ChronocodeVO) => {
      this.simulatedChronocodes.set(chr.code, chr);
    });
  }

  /** retourne l'élément précédent dans la pile */
  public getPreviousChrono(chrono: ChronocodeVO): ChronocodeVO {
    let result: ChronocodeVO = null;
    const meIdx: number = this.customPeriods.indexOf(chrono);
    if (meIdx > 0) {
      result = this.customPeriods[meIdx - 1];
    }
    return result;
  }

  /** retourne l'élément suivant dans la pile */
  public getNextChrono(chrono: ChronocodeVO): ChronocodeVO {
    let result: ChronocodeVO = null;
    const meIdx: number = this.customPeriods.indexOf(chrono);
    if (meIdx < this.customPeriods.length - 1) {
      result = this.customPeriods[meIdx + 1];
    }
    return result;
  }

  /**
   * suppression d'un chrono. Combler le vide sur l'enfant précédent
   * @param chrono à supprimer
   * @return l'élément précédent, modifié
   */
  public deleChrono(chrono: ChronocodeVO): ChronocodeVO {
    const previous: ChronocodeVO = this.getPreviousChrono(chrono);
    previous.endDate = chrono.endDate;
    this.customPeriods.splice(this.customPeriods.indexOf(chrono), 1);
    return previous;
  }

  /**
   * modification des dates d'un chrono
   * @param chrono le chrono modifié
   * @param start true si startDate, false = endDate
   */
  public alterChronoDate(chrono: ChronocodeVO, start: boolean): ChronocodeVO {
    let otherUpdated: ChronocodeVO = null;
    if (start) {
      otherUpdated = this.getPreviousChrono(chrono);
      otherUpdated.endDate = WizDateUtils.dateToNumber(
        WizDateUtils.add(WizDateUtils.numberToDate(chrono.startDate), { days: -1 }) as Date
      );
    } else {
      otherUpdated = this.getNextChrono(chrono);
      otherUpdated.startDate = WizDateUtils.dateToNumber(
        WizDateUtils.add(WizDateUtils.numberToDate(chrono.endDate), { days: +1 }) as Date
      );
    }
    this.computeMaxTimeWeight();
    return otherUpdated;
  }

  /** sur mise à jour des éléments custom, calcul du MaxWeight pur autoriser ou pas des enfants */
  private computeMaxTimeWeight(): void {
    let maxTimeWeight: number = NaN;

    if (!!this.customPeriods && this.customPeriods.length > 0) {
      // gestion custom
      this.customPeriods.forEach((chro: ChronocodeVO) => {
        const startDate = WizDateUtils.numberToDate(chro.startDate);
        const endDate = WizDateUtils.numberToDate(chro.endDate);
        const tmpWeight = WizDateUtils.dayDiff(startDate, endDate);
        if (isNaN(maxTimeWeight) || tmpWeight < maxTimeWeight) {
          maxTimeWeight = tmpWeight;
        }
      });
    } else {
      // gestion simple
      maxTimeWeight = this.getTemporalWeight(this.parentTemplate);
    }
    this.maxTimeWeight = maxTimeWeight;
  }

  /** contrôle de l'unicité de code des enfants */
  public checkCodeUnicity(chrono: ChronocodeVO): WizValidationError {
    let error: WizValidationError = null;
    for (const chr of this.customPeriods) {
      if (chr !== chrono && chr.shortCode === chrono.shortCode && chr.parentCode === chrono.parentCode) {
        error = {
          wizError: '$.chronocode.error.code.unique.$',
          wizErrorParams: [],
        };
      }
    }
    return error;
  }

  /** Libellé traductible */
  private _labelVO: TranslatableLabelVO;
  public get labelVO(): TranslatableLabelVO {
    if (!this._labelVO) {
      this._labelVO = this.model.localeEngine.newTranslatableVO(this, 'label');
    }
    return this._labelVO;
  }

}
